import { Target, Heart, Lightbulb, Award, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: "Community-Centered",
      description: "We prioritize the unique needs and cultural values of tribal communities and rural populations, ensuring our solutions are grounded in community wisdom."
    },
    {
      icon: Target,
      title: "Mission-Driven",
      description: "Our work is guided by a commitment to health equity, reducing disparities, and improving outcomes for underserved populations."
    },
    {
      icon: Lightbulb,
      title: "Evidence-Based",
      description: "We apply rigorous research methods, data analytics, and proven interventions to deliver measurable health improvements."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We maintain the highest standards of professional service, cultural competency, and ethical practice in all our work."
    }
  ];

  return (
    <div className="flex-1" data-testid="about-page">
      {/* Header */}
      <section className="bg-[primary] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4"
            className="font-heading"
            data-testid="about-heading"
          >
            About Us
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl">
            Strengthening health outcomes through data-driven, community-centered solutions
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-primary font-heading">
                Our Mission
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Population, Community, Public Health Services Center, Inc. exists to strengthen health outcomes, improve program performance, and build data-driven decision-making capacity for tribal communities, rural regions, and healthcare organizations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                We deliver comprehensive, culturally-grounded solutions that address the unique health challenges facing underserved populations across the nation.
              </p>
              <Link to="/about/founder" className="text-sm text-gray-500 hover:text-primary transition-colors inline-flex items-center gap-1">
                About Our Founder
                <ArrowRight size={14} />
              </Link>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-primary font-heading">
                Our Approach
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We combine rigorous evaluation methods, advanced data analytics, and community engagement to create sustainable health improvements. Our team brings expertise in population health, program evaluation, community health assessment, and health education.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Through partnerships with tribal leaders, healthcare providers, and community organizations, we design solutions that respect cultural values while advancing evidence-based practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F3] to-white"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-primary/10 rounded-full mb-4">
              <span className="text-primary text-sm font-semibold">Core Principles</span>
            </div>
            <h2 className="text-4xl font-bold mb-4 text-neutral-charcoal font-heading">Our Values</h2>
            <p className="text-lg text-neutral-gray max-w-2xl mx-auto">
              The principles that guide our work and partnerships
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              const colors = ['bg-primary', 'bg-accent-sage', 'bg-accent-sand', 'bg-accent-clay'];
              const borderColors = ['border-primary', 'border-accent-sage', 'border-accent-sand', 'border-accent-clay'];
              return (
                <Card key={index} className={`border-2 ${borderColors[index]} shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white`}>
                  <CardContent className="p-8 text-center">
                    <div className={`w-20 h-20 ${colors[index]} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                      <IconComponent className="text-white" size={36} />
                    </div>
                    <h3 className="text-xl font-bold mb-4 text-neutral-charcoal font-heading">
                      {value.title}
                    </h3>
                    <p className="text-sm text-neutral-gray leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" className="font-heading">Who We Serve</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-[primary]" className="font-heading">
                  Tribal Communities
                </h3>
                <p className="text-gray-700 text-sm">
                  We partner with tribal nations, Indian Health Service facilities, and tribal health programs to address unique health challenges through culturally-appropriate solutions.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-[primary]" className="font-heading">
                  Rural Healthcare
                </h3>
                <p className="text-gray-700 text-sm">
                  We support rural hospitals, clinics, and public health departments in building capacity for data-driven decision-making and program improvement.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-[primary]" className="font-heading">
                  Healthcare Organizations
                </h3>
                <p className="text-gray-700 text-sm">
                  We work with nonprofit health organizations, community health centers, and public health agencies to strengthen evaluation and community health programming.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.2) 35px, rgba(255,255,255,.2) 70px)'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 font-heading">
            Partner With Us
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Let&apos;s work together to strengthen health outcomes in your community.
          </p>
          <a href="/contact">
            <button className="px-12 py-5 bg-white text-primary rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105" data-testid="about-cta-button">
              Contact Us Today
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
